import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import type { Question, AnswerResult, PlayerAnswerStatus, FinalScore } from '../types';
import i18n from '@/i18n';

// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useGameStore = defineStore('game', () => {
  const authStore = useAuthStore();

  // --- State ---
  const currentImageUrl = ref<string | null>(null);
  const currentImageFrame = ref(0);
  const totalImageFrames = ref(0);
  const websocket = ref<WebSocket | null>(null);

  let imageFrameTimer: ReturnType<typeof setInterval> | null = null;
  const IMAGE_FRAME_INTERVAL = 1000;

  const questions = ref<Question[]>([]);
  const currentQuestionIndex = ref(0);

  const answerResult = ref<AnswerResult | null>(null);
  const isAnswered = ref(false);
  const gameEndMessage = ref<string | null>(null);

  const playerScores = ref<Record<string, number>>({});
  const playersAnsweredStatus = ref<Record<string, PlayerAnswerStatus>>({});
  const finalScores = ref<FinalScore[]>([]);
  const isGameFinishedFlag = ref(false);
  const singlePlayerTotalScore = ref(0);

  // UX Features State
  const answerStats = ref<Record<string, number> | null>(null);
  const leaderboard = ref<Array<{ user_id: string, score: number, rank: number }> | null>(null);
  const answeredCount = ref(0);
  const totalActivePlayers = ref(0);
  const totalQuestions = ref(0);
  const currentDisplayQuestionNumber = ref(1);

  // Countdown State
  // 問題開始前のカウントダウン中かどうか
  const isCountdownActive = ref(false);
  // カウントダウンの残り秒数
  const countdownValue = ref(0);
  // カウントダウン用のタイマーID
  let countdownTimer: ReturnType<typeof setInterval> | null = null;

  // --- Getters (Computed) ---
  const currentQuestion = computed(() => {
    const question = (questions.value.length > 0 && currentQuestionIndex.value < questions.value.length)
      ? questions.value[currentQuestionIndex.value]
      : null;
    return question;
  });

  const isGameFinished = computed(() => {
    return isGameFinishedFlag.value;
  });

  const currentZoomLevel = computed(() => {
    if (!currentQuestion.value || !currentQuestion.value.game_details || !currentQuestion.value.game_details.settings.zoom_level_sequence) {
      return 100; // Default to 100% (zoomed in) if not available
    }
    const levels = currentQuestion.value.game_details.settings.zoom_level_sequence;
    // levels are strings in backend constants but JSON might be numbers.
    // ensure index is within bounds
    if (currentImageFrame.value >= 0 && currentImageFrame.value < levels.length) {
      return Number(levels[currentImageFrame.value]);
    }
    return 0; // Default fallback
  });

  // --- Actions ---
  const ANSWER_TIME_LIMIT = 10.0; // Changed to float for consistency
  const remainingTime = ref(ANSWER_TIME_LIMIT);
  let animationFrameId: number | null = null;
  let startTime: number | null = null;

  const startTimer = () => {
    remainingTime.value = ANSWER_TIME_LIMIT;
    startTime = performance.now();

    // Stop any existing timer
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }

    const updateTimer = () => {
      if (startTime === null) return;

      const currentTime = performance.now();
      const elapsed = (currentTime - startTime) / 1000;
      const timeLeft = Math.max(0, ANSWER_TIME_LIMIT - elapsed);

      remainingTime.value = timeLeft;

      if (timeLeft > 0) {
        animationFrameId = requestAnimationFrame(updateTimer);
      } else {
        stopTimer();
        handleTimeUp();
      }
    };

    animationFrameId = requestAnimationFrame(updateTimer);
  };

  const stopTimer = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    startTime = null;
  };

  // const proceedToNextQuestion = (delay: number) => {
  //   setTimeout(() => {
  //     currentQuestionIndex.value++;
  //     answerResult.value = null;
  //     isAnswered.value = false;
  //     playersAnsweredStatus.value = {};
  //   }, delay);
  // };

  function resetState() {
    questions.value = [];
    currentQuestionIndex.value = 0;
    answerResult.value = null;
    isAnswered.value = false;
    gameEndMessage.value = null;
    playerScores.value = {};
    playersAnsweredStatus.value = {};
    finalScores.value = [];
    isGameFinishedFlag.value = false;
    singlePlayerTotalScore.value = 0;
    revokeImageUrl();
    currentImageUrl.value = null;
    currentImageFrame.value = 0;
    totalImageFrames.value = 0;
    stopTimer();
    stopImageFrameTimer();
    stopTimer();
    stopImageFrameTimer();
    stopImageFrameTimer();
    stopCountdown();
    answerStats.value = null;
    leaderboard.value = null;
    answeredCount.value = 0;
    totalActivePlayers.value = 0;
    totalQuestions.value = 0;
    currentDisplayQuestionNumber.value = 1;
    stopCountdown();
    isCountdownActive.value = false;
    countdownValue.value = 0;
  }

  function revokeImageUrl() {
    if (currentImageUrl.value && currentImageUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(currentImageUrl.value);
    }
  }

  async function startGameViaWebSocket(roomId: string, gameType: string = 'zoom_out') {
    resetState();
    return new Promise<void>((resolve, reject) => {
      if (!websocket.value || websocket.value.readyState !== WebSocket.OPEN) {
        console.error('WebSocket is not connected.');
        reject(new Error('WebSocket is not connected.'));
        return;
      }

      sendWebSocketMessage({
        type: 'start_game',
        room_id: roomId,
        game_type: gameType,
      });
      console.log('startGameViaWebSocket: sent start_game message:', { roomId, gameType });
      resolve();
    });
  }

  async function selectAnswer(choiceId: number) {
    if (!currentQuestion.value) {
      console.warn('selectAnswer: currentQuestion is null, skipping.');
      return;
    }
    if (isAnswered.value) {
      console.warn('selectAnswer: already answered, skipping.');
      return;
    }
    if (isCountdownActive.value) {
      console.warn('selectAnswer: countdown active, skipping.');
      return;
    }

    isAnswered.value = true;
    stopTimer();

    const message = {
      type: "answer_submitted",
      question_id: currentQuestion.value.id,
      choice_id: choiceId,
      time_taken: ANSWER_TIME_LIMIT - remainingTime.value,
    };
    sendWebSocketMessage(message);
    console.log('selectAnswer: WebSocket message sent.');
  }

  async function handleTimeUp() {
    if (!currentQuestion.value || isAnswered.value) return;

    isAnswered.value = true;
    stopTimer();

    sendWebSocketMessage({
      type: "answer_submitted",
      question_id: currentQuestion.value.id,
      choice_id: null,
      time_taken: ANSWER_TIME_LIMIT,
    });
  }

  async function connectWebSocket(roomId: string) {
    return new Promise<void>((resolve, reject) => {
      if (websocket.value && websocket.value.readyState === WebSocket.OPEN) {
        console.log('WebSocket is already connected.');
        resolve();
        return;
      }

      let API_WS_BASE_URL = import.meta.env.VITE_API_WS_BASE_URL;

      if (!API_WS_BASE_URL) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        API_WS_BASE_URL = `${protocol}//${host}`;
      }


      const token = localStorage.getItem('token');
      let wsUrl = `${API_WS_BASE_URL}/ws/rooms/${roomId}/`;

      if (token) {
        wsUrl += `?token=${token}`;
      }

      websocket.value = new WebSocket(wsUrl);
      websocket.value.binaryType = 'arraybuffer';

      websocket.value.onopen = () => {
        console.log('WebSocket connection established:', wsUrl);
        resolve();
      };

      websocket.value.onmessage = async (event) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any;
        let imageUrl: string | null = null;
        let arrayBuffer: ArrayBuffer | undefined;

        try {
          if (event.data instanceof ArrayBuffer) {
            arrayBuffer = event.data;
          } else if (event.data instanceof Blob) {
            arrayBuffer = await event.data.arrayBuffer();
          } else if (typeof event.data === 'string') {
            data = JSON.parse(event.data);
          } else {
            console.error('[ERROR] Unexpected data type:', event.data);
            return;
          }



          if (arrayBuffer) {
            const dataView = new DataView(arrayBuffer);
            const headerLength = dataView.getUint32(0, false); // Big Endian
            const metadataBytes = new Uint8Array(arrayBuffer, 4, headerLength);
            const metadataString = new TextDecoder("utf-8").decode(metadataBytes);
            data = JSON.parse(metadataString);


            const imageBytes = new Uint8Array(arrayBuffer, 4 + headerLength);
            const blob = new Blob([imageBytes], { type: 'image/webp' });
            imageUrl = URL.createObjectURL(blob);
          }
        } catch (e) {
          console.error('[ERROR] Failed to process WebSocket message:', e);
          return;
        }

        const logData = { ...data };
        if (imageUrl) {
          logData.image_url = '[Blob URL]';
        }

        switch (data.type) {
          case 'send_question_data':
            setQuestionData(data, imageUrl);
            break;
          case 'send_image_frame':
            setImageFrame(data, imageUrl);
            break;
          case 'answer_result':
            handleAnswerResult(data, imageUrl);
            break;
          case 'game_finished':
            gameFinished(data);
            break;
          case 'player_answered_update':
            updatePlayerAnsweredStatus(data);
            break;
          case 'answer_result_group':
            handleAnswerResultGroup(data, imageUrl);
            break;
          case 'answer_stats':
            handleAnswerStats(data);
            break;
          case 'leaderboard_update':
          case 'leaderboard_updated': // Handle both single/multi player
            handleLeaderboardUpdate(data);
            break;
          case 'game_started':
            break;
          case 'error':
            console.error('Error received in gameStore:', data.message);
            break;
          default:
            console.warn('Unknown WebSocket message type (gameStore):', data.type, 'Data:', JSON.stringify(logData, null, 2));
        }
      };

      websocket.value.onerror = (error) => {
        console.error('WebSocket error occurred:', error);
        reject(error);
      };
    });
  }

  function disconnectWebSocket() {
    if (websocket.value && websocket.value.readyState === WebSocket.OPEN) {
      websocket.value.close();
      websocket.value = null;
      console.log('WebSocket connection closed.');
    }
    stopImageFrameTimer();
    revokeImageUrl();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function sendWebSocketMessage(message: any) {
    if (websocket.value && websocket.value.readyState === WebSocket.OPEN) {
      websocket.value.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected.');
    }
  }

  const isHost = ref(false);

  function setIsHost(value: boolean) {
    isHost.value = value;
  }

  // Action to update player name in single player mode
  async function updatePlayerName(name: string) {
    sendWebSocketMessage({
      type: 'update_name',
      name: name
    });

    // Optimistic update
    const myScoreIndex = finalScores.value.findIndex(s => s.is_me);
    if (myScoreIndex !== -1) {
      if (finalScores.value[myScoreIndex]) {
        finalScores.value[myScoreIndex].username = name;
      }
    }
  }

  function startImageFrameTimer() {
    stopImageFrameTimer();
    if (!currentQuestion.value || currentImageFrame.value >= totalImageFrames.value - 1) {
      return;
    }

    if (currentQuestion.value.room_id !== 'single_player_room' && !isHost.value) {
      return;
    }

    imageFrameTimer = setInterval(() => {
      const nextFrameIndexToRequest = currentImageFrame.value + 1;

      if (nextFrameIndexToRequest < totalImageFrames.value) {
        const message = {
          type: "next_image_frame",
          room_id: currentQuestion.value!.room_id || 'single_player_room',
          question_id: currentQuestion.value!.id,
          current_image_index: nextFrameIndexToRequest,
        };
        // const zoomLevelSequence = currentQuestion.value!.game_details.settings.zoom_level_sequence;
        sendWebSocketMessage(message);
      } else {
        stopImageFrameTimer();
      }
    }, IMAGE_FRAME_INTERVAL);
    console.log('Image frame timer started.');
  }

  function stopImageFrameTimer() {
    if (imageFrameTimer) {
      clearInterval(imageFrameTimer);
      imageFrameTimer = null;
      console.log('Image frame timer stopped.');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function setQuestionData(data: any, imageUrl: string | null) {

    questions.value = [data.question];
    currentQuestionIndex.value = 0;

    // Update display info
    if (data.question.total_questions) {
      totalQuestions.value = data.question.total_questions;
    }
    if (data.question.current_question_index) {
      currentDisplayQuestionNumber.value = data.question.current_question_index;
    } else {
      // Fallback or increment if logic allows, but server source is best
    }

    if (data.question.total_players) {
      totalActivePlayers.value = data.question.total_players;
    }

    revokeImageUrl();
    currentImageUrl.value = imageUrl;

    currentImageFrame.value = data.image_frame;
    totalImageFrames.value = data.total_images;

    answerResult.value = null;

    stopTimer();
    isAnswered.value = false;

    gameEndMessage.value = null;
    isGameFinishedFlag.value = false;
    playersAnsweredStatus.value = {};
    playersAnsweredStatus.value = {};
    playerScores.value = {};
    answerStats.value = null;
    // Don't clear leaderboard here if we want to show it cumulatively, 
    // but the backend sends full leaderboard update anyway.
    leaderboard.value = null;
    answeredCount.value = 0;
    // totalActivePlayers.value = 0; // Removed to prevent overwriting value from backend

    if (data.question.room_id === 'single_player_room' && currentQuestionIndex.value === 0) {
      singlePlayerTotalScore.value = 0;
    }


    // Start countdown instead of timer immediately
    // サーバーからカウントダウン時間が指定されている場合は、すぐに回答タイマーを開始せず
    // カウントダウンを開始する
    const countdownDuration = data.countdown_duration || 0;

    // Reset timer display to max immediately so it doesn't show old question's time
    // remainingTime.value = ANSWER_TIME_LIMIT; // Moved to top

    if (countdownDuration > 0) {
      isCountdownActive.value = true;
      countdownValue.value = countdownDuration;
      startCountdown();
    } else {
      startTimer();
    }
  }

  /**
   * カウントダウンを開始する
   * カウントダウン終了後に回答タイマーを開始する
   */
  function startCountdown() {
    stopCountdown();
    console.log('Starting countdown:', countdownValue.value);

    countdownTimer = setInterval(() => {
      countdownValue.value--;
      if (countdownValue.value <= 0) {
        stopCountdown();
        isCountdownActive.value = false;
        startTimer(); // Start game timer after countdown
      }
    }, 1000); // 1 second interval
  }

  /**
   * カウントダウンを停止する
   */
  function stopCountdown() {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function setImageFrame(data: any, imageUrl: string | null) {
    if (currentQuestion.value && currentQuestion.value.id === data.question_id) {
      revokeImageUrl();
      currentImageUrl.value = imageUrl;

      currentImageFrame.value = data.image_frame;
      totalImageFrames.value = data.total_images;

      if (currentImageFrame.value >= totalImageFrames.value - 1) {
        stopImageFrameTimer();
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleAnswerResult(data: any, imageUrl: string | null) {
    if (currentQuestion.value && currentQuestion.value.room_id === 'single_player_room') {
      if (imageUrl) {
        revokeImageUrl();
        currentImageUrl.value = imageUrl;
      }
      const isCorrect = data.is_correct;
      let correctChoiceText = data.correct_choice_text;

      // Ensure we display the localized text for the correct answer
      if (currentQuestion.value && data.correct_choice_id) {
        const correctChoice = currentQuestion.value.choices.find(c => c.id === data.correct_choice_id);
        if (correctChoice) {
          // Reuse logic from AnswerChoices or duplicate it simply here:
          const locale = i18n.global.locale.value;
          if (correctChoice.translations && correctChoice.translations[locale]) {
            correctChoiceText = correctChoice.translations[locale].text;
          } else {
            correctChoiceText = correctChoice.text;
          }
        }
      }

      const scoreEarned = data.score_earned || 0;

      singlePlayerTotalScore.value += scoreEarned;

      if (isCorrect) {
        answerResult.value = { isCorrect: true, message: i18n.global.t('game.correct_message_score', { score: scoreEarned }) };
      } else {
        answerResult.value = { isCorrect: false, message: i18n.global.t('game.incorrect_message', { answer: correctChoiceText }) };
      }
      stopImageFrameTimer();

      setTimeout(() => {
        gameEndMessage.value = null;
        // proceedToNextQuestion(0); // Backend sends next question, which resets state. calling this corrupts index.
      }, 1000);
    } else {
      console.log("handleAnswerResult: Skipping as it is multiplayer or room_id is unknown.");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleAnswerResultGroup(data: any, imageUrl: string | null) {
    isAnswered.value = true;
    stopTimer();
    stopImageFrameTimer();

    // Hide stats when result is shown
    answerStats.value = null;

    if (imageUrl) {
      revokeImageUrl();
      currentImageUrl.value = imageUrl;
    }

    // Ensure we display the localized text for the correct answer
    let correctChoiceText = data.correct_choice_text;
    if (currentQuestion.value && data.correct_choice_id) {
      const correctChoice = currentQuestion.value.choices.find(c => c.id === data.correct_choice_id);
      if (correctChoice) {
        const locale = i18n.global.locale.value;
        if (correctChoice.translations && correctChoice.translations[locale]) {
          correctChoiceText = correctChoice.translations[locale].text;
        } else {
          correctChoiceText = correctChoice.text;
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const myResult = data.results.find((res: any) => String(res.user_id) === String(authStore.user?.id));

    if (myResult) {
      answerResult.value = {
        type: 'my_group_result',
        isCorrect: myResult.is_correct,
        message: myResult.is_correct ? i18n.global.t('game.correct_simple') : i18n.global.t('game.incorrect_message', { answer: correctChoiceText }),
        scoreEarned: myResult.score_earned,
        correctChoiceText: correctChoiceText,
        username: myResult.username,
      };
    } else {
      console.error('gameStore: handleAnswerResultGroup - My result not found.');
      answerResult.value = {
        type: 'my_group_result',
        isCorrect: false,
        message: i18n.global.t('game.error_result', { answer: correctChoiceText }),
        scoreEarned: 0,
        correctChoiceText: correctChoiceText,
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function updatePlayerAnsweredStatus(data: any) {
    playersAnsweredStatus.value = {
      ...playersAnsweredStatus.value,
      [data.user_id]: { username: data.username, answered: true, score: data.score_earned },
    };
    answeredCount.value = data.answered_count;
    totalActivePlayers.value = data.total_players;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleAnswerStats(data: any) {
    answerStats.value = data.stats;
    stopImageFrameTimer();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleLeaderboardUpdate(data: any) {
    answerStats.value = null; // Hide stats
    leaderboard.value = data.leaderboard;
    // Also update finalScores if game is finished to update the view
    if (isGameFinishedFlag.value) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      finalScores.value = (data.leaderboard || []).map((s: any) => ({
        ...s,
        is_me: authStore.user && String(s.user_id) === String(authStore.user.id)
      }));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  function allImagesSent(data: any) {

  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gameFinished(data: any) {
    gameEndMessage.value = data.message || 'ゲーム終了！';
    const rawScores = data.leaderboard || data.final_scores || [];
    // Backend already provides is_me based on target_score_obj.id, so we should trust it if available.
    // However, for safety or if logic differs, we can re-evaluate.
    // Ideally, the backend knows exactly which score record is 'this' one.
    // In Single Player, multiple scores might exist for the same user.
    // We should rely on the backend's `is_me` flag which is set based on the score ID just created.

    // Ensure rank is present (Backend get_final_scores_for_session for multiplayer might not send it)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    finalScores.value = rawScores.map((s: any, index: number) => ({
      ...s,
      rank: s.rank !== undefined ? s.rank : index + 1,
      is_me: typeof s.is_me !== 'undefined' ? s.is_me : (authStore.user && String(s.user_id) === String(authStore.user.id))
    }));
    isGameFinishedFlag.value = true;
    stopImageFrameTimer();
    stopTimer();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gameStarted(data: any) {
    console.log('gameStore: gameStarted called', data);
    resetState();
    if (data.total_questions) {
      totalQuestions.value = data.total_questions;
    }
    if (data.total_players) {
      totalActivePlayers.value = data.total_players;
    }
  }

  function setWebSocket(ws: WebSocket | null) {
    if (ws === null) {
      // Normal cleanup
      websocket.value = null;
      return;
    }
    if (ws && ws instanceof WebSocket) {
      websocket.value = ws;
      websocket.value.binaryType = 'arraybuffer';
      console.log('gameStore.setWebSocket: WebSocket instance set.');

    } else {
      console.error('gameStore.setWebSocket: Invalid WebSocket instance:', ws);
      websocket.value = null;
    }
  }

  return {
    // State
    questions,
    currentQuestionIndex,
    answerResult,
    isAnswered,
    gameEndMessage,
    currentImageUrl,
    currentImageFrame,
    totalImageFrames,
    remainingTime,
    playerScores,
    playersAnsweredStatus,
    finalScores,
    isGameFinishedFlag,
    singlePlayerTotalScore,
    websocket,
    answerStats,
    leaderboard,
    answeredCount,
    totalActivePlayers,
    totalQuestions,
    currentDisplayQuestionNumber,
    isCountdownActive,
    countdownValue,
    // Getters
    currentQuestion,
    isGameFinished,
    isHost,
    currentZoomLevel,
    // Actions
    startGameViaWebSocket,
    selectAnswer,
    handleTimeUp,
    connectWebSocket,
    disconnectWebSocket,
    sendWebSocketMessage,
    setQuestionData,
    setImageFrame,
    handleAnswerResult,
    startTimer,
    stopTimer,
    startImageFrameTimer,
    stopImageFrameTimer,
    allImagesSent,
    gameFinished,
    gameStarted,
    setWebSocket,
    updatePlayerAnsweredStatus,
    handleAnswerResultGroup,
    handleAnswerStats,
    handleLeaderboardUpdate,
    setIsHost,
    resetState,
    updatePlayerName,
  };
});
